import { useState } from "react";
import type { BulkPriceResultDto } from "@semo/api-contracts";

import type { BulkPriceInput } from "../items/application/hooks/useMyStorefrontOfferings";

interface Props {
  storefrontName: string;
  unpricedCount: number;
  onRun: (input: BulkPriceInput) => Promise<{ data?: BulkPriceResultDto }>;
  onClose: () => void;
}

type Mode = BulkPriceInput["mode"];

const MODES: { value: Mode; label: string; hint: string }[] = [
  {
    value: "link",
    label: "내 단가에 연동",
    hint: "앞으로 내 단가를 고치면 몰 가격도 같이 바뀝니다. 한 번만 걸어 두면 됩니다.",
  },
  {
    value: "copy_base",
    label: "지금 내 단가를 복사",
    hint: "이 순간의 내 단가를 몰 가격으로 적어 넣습니다. 이후 내 단가가 바뀌어도 따라가지 않습니다.",
  },
  {
    value: "unlink",
    label: "연동 해제 (지금 값으로 고정)",
    hint: "연동 중인 상품을 지금 가격 그대로 굳힙니다.",
  },
  {
    value: "clear",
    label: "몰 가격 지우기",
    hint: "노출에서 빠집니다. 승인은 남으므로 값을 다시 넣으면 살아납니다.",
  },
];

const won = (n: number | null | undefined) =>
  n == null ? "-" : `${n.toLocaleString("ko-KR")}원`;

/**
 * 몰 가격 일괄 설정.
 *
 * 승인된 상품이 수천 건이면 칸을 하나씩 채우는 것은 현실적이지 않다. 실제로도
 * 사람들은 «내 단가와 같은 값» 을 손으로 옮겨 적고 있었다(실측 53/55).
 * 그래서 기본값이 «연동» 이다 — 복사는 다음 달에 또 해야 하지만 연동은 한 번이다.
 */
export function StorefrontBulkPriceDialog({
  storefrontName,
  unpricedCount,
  onRun,
  onClose,
}: Props) {
  const [mode, setMode] = useState<Mode>("link");
  const [markupPercent, setMarkupPercent] = useState("0");
  const [overwrite, setOverwrite] = useState(false);
  const [preview, setPreview] = useState<BulkPriceResultDto | null>(null);
  const [busy, setBusy] = useState(false);

  const markupBp = Math.round((Number(markupPercent) || 0) * 100);
  const invalidate = () => setPreview(null);

  const run = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const res = await onRun({ mode, markupBp, overwrite, dryRun });
      if (dryRun) setPreview(res?.data ?? null);
      else onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold">{storefrontName} 가격 일괄 설정</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          이 몰에 승인된 내 상품 전체가 대상입니다
          {unpricedCount > 0
            ? ` (가격 미입력 ${unpricedCount.toLocaleString("ko-KR")}건)`
            : ""}
          .
        </p>

        <fieldset className="mt-4 rounded-xl border border-border p-3">
          <legend className="px-1 text-sm font-medium">방식</legend>
          {MODES.map((m) => (
            <label key={m.value} className="flex items-start gap-2 py-1 text-sm">
              <input
                type="radio"
                checked={mode === m.value}
                onChange={() => {
                  setMode(m.value);
                  invalidate();
                }}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{m.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {m.hint}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {mode === "link" || mode === "copy_base" ? (
          <label className="mt-3 flex items-center gap-2 text-sm">
            가산율
            <input
              value={markupPercent}
              onChange={(e) => {
                setMarkupPercent(e.target.value);
                invalidate();
              }}
              inputMode="decimal"
              className="w-20 rounded-lg border border-border px-2 py-1 text-right tabular-nums"
            />
            %
            <span className="text-xs text-muted-foreground">
              0 이면 내 단가 그대로 · 10원 절사
            </span>
          </label>
        ) : null}

        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => {
              setOverwrite(e.target.checked);
              invalidate();
            }}
            className="mt-1"
          />
          <span>
            <span className="font-medium">이미 넣은 가격도 덮어쓰기</span>
            <span className="block text-xs text-muted-foreground">
              기본은 <b>빈 칸만</b> 채웁니다. 손으로 정한 값이 사라지지 않게
              하기 위해서입니다.
            </span>
          </span>
        </label>

        {preview ? (
          <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 text-sm">
            <p>
              대상 <b className="tabular-nums">{preview.targets.toLocaleString("ko-KR")}</b>건
              중 <b className="tabular-nums text-emerald-700">
                {preview.willChange.toLocaleString("ko-KR")}
              </b>
              건이 바뀝니다
              {preview.skipped > 0
                ? ` · 건너뜀 ${preview.skipped.toLocaleString("ko-KR")}건`
                : ""}
              {preview.noBasePrice > 0
                ? ` · 내 단가 없음 ${preview.noBasePrice.toLocaleString("ko-KR")}건`
                : ""}
            </p>

            {preview.samples.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-medium text-amber-700">
                  변동폭이 큰 순 {preview.samples.length}건
                </p>
                <table className="mt-1 w-full text-xs">
                  <tbody>
                    {preview.samples.map((s, i) => (
                      <tr
                        key={`${s.name}-${i}`}
                        className="border-t border-border/40"
                      >
                        <td className="py-1 pr-2">{s.name}</td>
                        <td className="py-1 text-right tabular-nums text-muted-foreground">
                          {won(s.oldPrice)}
                        </td>
                        <td className="px-1 py-1 text-center text-muted-foreground">
                          →
                        </td>
                        <td className="py-1 text-right font-medium tabular-nums">
                          {won(s.newPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm"
          >
            닫기
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(true)}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium disabled:opacity-40"
          >
            미리보기
          </button>
          <button
            type="button"
            disabled={!preview || preview.willChange === 0 || busy}
            onClick={() => void run(false)}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {preview
              ? `${preview.willChange.toLocaleString("ko-KR")}건 적용`
              : "미리보기 먼저"}
          </button>
        </div>
      </div>
    </div>
  );
}
